import { useMemo, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'src/store';
import type { AskSalahSubscription } from 'src/types/askSalah';
import { getActiveSal125Subscription } from 'src/free-trial/helpers';
import { getUserAskSalahSubscription } from 'src/api/askSalah';

/**
 * Hook to access Ask Salah subscription data and status
 * 
 * Returns:
 * - subscription: Full subscription object or null
 * - isActive: Boolean if subscription is active and not expired
 * - planType: 'trial', 'basic', 'premium', or null
 * - billingCycle: 'monthly', 'yearly', or null
 * - renewalDate: Next renewal date
 * - canAccessVoice: Boolean (Premium feature check)
 * - hasAskSalahAccess: Boolean (has any active Ask Salah subscription)
 * - hasSAL125Subscription: Boolean (has active SAL125 subscription)
 * - canAccessPaidPlans: Boolean (has paid SAL125 subscription, not free trial)
 * - refreshSubscription: Function to manually refresh subscription data
 * - isLoading: Boolean indicating if subscription is being fetched
 */
export const useAskSalahSubscription = () => {
  const user = useSelector((state) => state.auth.user);
  const [fetchedSubscription, setFetchedSubscription] = useState<AskSalahSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Ask Salah subscription data
  const fetchSubscription = useCallback(async () => {
    if (!user) return null;
    
    setIsLoading(true);
    try {
      const data = await getUserAskSalahSubscription();
      console.log('[Ask Salah] Fetched subscription data:', data);
      setFetchedSubscription(data);
      return data;
    } catch (error) {
      console.error('Error fetching Ask Salah subscription:', error);
      setFetchedSubscription(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user, fetchSubscription]);

  const result = useMemo(() => {
    // Use fetched subscription if available, otherwise fall back to user state
    const subscription = fetchedSubscription || user?.askSalahSubscription;

    // Check if subscription exists and is active
    // IMPORTANT: Handle both string and Date objects for endDate
    let isActive = false;
    if (subscription) {
      const endDate = subscription.endDate instanceof Date 
        ? subscription.endDate 
        : new Date(subscription.endDate);
      const now = new Date();
      
      isActive = 
        subscription.status === 'active' &&
        endDate > now;
      
      console.log('[Ask Salah] Subscription check:', {
        hasSubscription: true,
        status: subscription.status,
        endDate: endDate.toISOString(),
        now: now.toISOString(),
        isActive,
        endDateType: typeof subscription.endDate,
      });
    } else {
      console.log('[Ask Salah] No subscription found');
    }

    const sal125Subscription = getActiveSal125Subscription(user);
    const hasSubscriptionInArray = !!sal125Subscription;
    const hasSubscriptionStatus =
      user?.subscriptionStatus === 'active' ||
      user?.subscriptionStatus === 'trial' ||
      user?.subscriptionStatus === 'free_trial' ||
      user?.subscriptionStatus === 'approved';

    const hasSAL125Subscription = hasSubscriptionInArray || hasSubscriptionStatus;

    const isSAL125FreeTrial = sal125Subscription?.type === 'free_trial';
    
    // User can access paid plans if they have SAL125 subscription and it's NOT a free trial
    const canAccessPaidPlans = hasSAL125Subscription && !isSAL125FreeTrial;

    const planKey = String(subscription?.planType ?? '').toLowerCase();
    const canAccessVoice = isActive && planKey === 'premium';

    const result = {
      subscription: isActive ? subscription || null : null,
      isActive,
      planType: isActive ? subscription?.planType || null : null,
      billingCycle: subscription?.billingCycle || null,
      renewalDate: subscription?.endDate || null,
      canAccessVoice,
      hasAskSalahAccess: isActive,
      hasSAL125Subscription,
      canAccessPaidPlans,
      sessionsUsed: subscription?.sessionsUsedThisMonth || 0,
      sessionsLimit: subscription?.sessionsLimit || 0,
      refreshSubscription: fetchSubscription,
      isLoading,
    };

    return result;
  }, [user, fetchedSubscription, fetchSubscription, isLoading]);

  return result;
};

